import { API_URL } from '../../../app-config/constants';
import type CreateMeetingRequest from '../../../types/CreateMeetingRequest';
import type CreateTalkingPointCommand from '../../../types/CreateTalkingPointCommand';
import type { DownloadableFile } from '../../../types/DownloadableFile';
import type EditTalkingPointCommand from '../../../types/EditTalkingPointCommand';
import type { MeetingNoteRequest } from '../../../types/MeetingContentDto';
import type MeetingDto from '../../../types/MeetingDto';
import type MeetingFromTemplateRequest from '../../../types/MeetingFromTemplateRequest';
import type MeetingNoteDto from '../../../types/MeetingNoteDto';
import type MeetingViewersDto from '../../../types/MeetingViewersDto';
import type TalkingPointDto from '../../../types/TalkingPointDto';
import type { FilePayload } from '../../react/features/Attachments/types';
import { get, patch, post, put, remove } from '../request';

const ENTRY = `${API_URL}/meetings`;

createMeeting.operation = 'CREATE';
createMeeting.invalidates = ['getMeetingsWithParticipants'];
export async function createMeeting(request: CreateMeetingRequest): Promise<MeetingDto> {
    return await post(ENTRY, { postData: { ...request } });
}

createMeetingFromTemplate.operation = 'CREATE';
createMeetingFromTemplate.invalidates = ['getMeetingsWithParticipants'];
export async function createMeetingFromTemplate(request: MeetingFromTemplateRequest): Promise<MeetingDto> {
    return await post(`${API_URL}/meeting-templates/create-meeting`, { postData: { ...request } });
}

getMeetingsWithParticipants.operation = 'READ';
export function getMeetingsWithParticipants(
    participantIds: [string, string],
    startDate?: string,
    endDate?: string
): Promise<MeetingDto[]> {
    return get(`${ENTRY}`, {
        getData: {
            participants: participantIds.join(','),
            startDateTime: startDate ? `${startDate}T00:00:00.000` : undefined,
            endDateTime: endDate ? `${endDate}T23:59:59.999` : undefined,
        },
    });
}

getMeeting.operation = 'READ';
export function getMeeting(id: string): Promise<MeetingDto> {
    return get(`${ENTRY}/${id}`);
}

deleteMeeting.operation = 'DELETE';
deleteMeeting.invalidates = ['getMeetingsWithParticipants', 'getMeeting'];
export function deleteMeeting(payload: Pick<MeetingDto, 'id'>): Promise<void> {
    return remove(`${ENTRY}/${payload.id}`);
}

setDate.operation = 'UPDATE';
setDate.invalidates = ['getMeetingsWithParticipants', 'getMeeting'];
export function setDate(payload: Pick<MeetingDto, 'id' | 'date'>): Promise<MeetingDto> {
    const { date, id } = payload;
    return patch(`${ENTRY}/${id}`, {
        postData: { date },
    });
}

setCalendarDate.operation = 'UPDATE';
setCalendarDate.invalidates = ['getMeetingsWithParticipants', 'getMeeting'];
export function setCalendarDate(payload: Pick<MeetingDto, 'id' | 'calendarDate'>): Promise<MeetingDto> {
    const { calendarDate, id } = payload;
    return patch(`${ENTRY}/${id}`, {
        postData: { calendarDate },
    });
}

setTitle.operation = 'UPDATE';
setTitle.invalidates = ['getMeetingsWithParticipants', 'getMeeting'];
export function setTitle(payload: Pick<MeetingDto, 'id' | 'title'>): Promise<MeetingDto> {
    const { title, id } = payload;
    return patch(`${ENTRY}/${id}`, { postData: { title } });
}

setVisibleTo.operation = 'UPDATE';
setVisibleTo.invalidates = ['getMeetingsWithParticipants', 'getMeeting'];
export function setVisibleTo(payload: Pick<MeetingDto, 'id' | 'visibleTo'>): Promise<MeetingDto> {
    const { visibleTo, id } = payload;
    return patch(`${ENTRY}/${id}`, { postData: { visibleTo } });
}

setVisibleTo.operation = 'UPDATE';
setVisibleTo.invalidates = ['getMeetingsWithParticipants', 'getMeeting'];
export function setTitleAndVisibleTo(payload: Pick<MeetingDto, 'id' | 'visibleTo' | 'title'>): Promise<MeetingDto> {
    const { title, visibleTo, id } = payload;
    return patch(`${ENTRY}/${id}`, { postData: { title, visibleTo } });
}

setCarryOverActionDateByParticipant.operation = 'UPDATE';
setCarryOverActionDateByParticipant.invalidates = ['getMeetingsWithParticipants', 'getMeeting'];
export function setCarryOverActionDateByParticipant(payload: Pick<MeetingDto, 'id'>): Promise<MeetingDto> {
    const { id } = payload;
    return patch(`${ENTRY}/${id}`, { postData: { carryOverActionDateByParticipant: {} } });
}

recordTemplateUsage.operation = 'NO_OPERATION';
export function recordTemplateUsage(payload: { meetingId: string; templateId: string }): Promise<void> {
    return post(`${API_URL}/meeting-templates-usage`, {
        postData: payload,
    });
}

shareMeeting.operation = 'UPDATE';
shareMeeting.invalidates = ['getMeetingsWithParticipants', 'getMeeting'];
export function shareMeeting(payload: Pick<MeetingDto, 'id'>): Promise<MeetingDto> {
    const { id } = payload;
    return patch(`${ENTRY}/${id}`, { postData: { isDraft: false } });
}

// BULK UPDATE VISIBILITY
bulkUpdateVisibility.operation = 'NO_OPERATION';
bulkUpdateVisibility.invalidates = ['getMeetingsWithParticipants', 'getMeeting'];
export function bulkUpdateVisibility(
    participantIds: [string, string],
    payload: MeetingViewersDto,
    mergeVisibility: boolean
) {
    return post<MeetingDto[]>(`${ENTRY}/visibility`, {
        getData: {
            participants: participantIds.join(','),
            mergeVisibility: mergeVisibility.toString(),
        },
        postData: payload,
    });
}

// TALKING POINTS

createTalkingPoints.operation = 'NO_OPERATION';
createTalkingPoints.invalidates = ['getMeetingsWithParticipants', 'getMeeting'];
export function createTalkingPoints(
    payload: (CreateTalkingPointCommand & { meetingId: string })[]
): Promise<TalkingPointDto[]> {
    return post(`${ENTRY}/${payload[0].meetingId}/talkingpoints`, {
        postData: payload.map(tp => {
            return { ...tp, private: tp.visibility === 'PRIVATE' };
        }),
    });
}

saveTalkingPoint.operation = 'NO_OPERATION';
saveTalkingPoint.invalidates = ['getMeetingsWithParticipants', 'getMeeting'];
export function saveTalkingPoint(payload: EditTalkingPointCommand): Promise<void> {
    return put(`${ENTRY}/${payload.meetingId}/talkingpoints/${payload.id}`, {
        postData: { ...payload, private: payload.visibility === 'PRIVATE' },
    });
}

checkTalkingPoint.operation = 'NO_OPERATION';
checkTalkingPoint.invalidates = ['getMeetingsWithParticipants', 'getMeeting'];
export function checkTalkingPoint(payload: Pick<TalkingPointDto, 'id' | 'meetingId' | 'checked'>) {
    return put(`${ENTRY}/${payload.meetingId}/talkingpoints/${payload.id}/check?checked=${!!payload.checked}`);
}

reorderTalkingPoint.operation = 'NO_OPERATION';
reorderTalkingPoint.invalidates = ['getMeetingsWithParticipants', 'getMeeting'];
export function reorderTalkingPoint(payload: {
    meetingId: string;
    authorId: string;
    order: string[];
}): Promise<TalkingPointDto[]> {
    return put(`${ENTRY}/${payload.meetingId}/talkingpoints`, { postData: payload });
}

deleteTalkingPoint.operation = 'NO_OPERATION';
deleteTalkingPoint.invalidates = ['getMeetingsWithParticipants', 'getMeeting'];
export function deleteTalkingPoint(payload: Pick<TalkingPointDto, 'id' | 'meetingId'>): Promise<void> {
    return remove(`${ENTRY}/${payload.meetingId}/talkingpoints/${payload.id}`);
}

// NOTES

const getNoteUrl = (note: Required<MeetingNoteRequest> | Pick<TalkingPointDto, 'id' | 'meetingId'>) =>
    `${ENTRY}/${note.meetingId}/notes/${note.id}`;

createNote.operation = 'NO_OPERATION';
createNote.invalidates = ['getMeetingsWithParticipants', 'getMeeting'];
export function createNote(payload: MeetingNoteRequest): Promise<MeetingNoteDto> {
    return post(`${ENTRY}/${payload.meetingId}/notes`, {
        postData: { ...payload, private: payload.visibility === 'PRIVATE' },
    });
}

saveNote.operation = 'NO_OPERATION';
saveNote.invalidates = ['getMeetingsWithParticipants', 'getMeeting'];
export function saveNote(note: Required<MeetingNoteRequest>): Promise<MeetingNoteDto> {
    const url = getNoteUrl(note);
    return put(url, { postData: { ...note, private: note.visibility === 'PRIVATE' } });
}

deleteNote.operation = 'NO_OPERATION';
deleteNote.invalidates = ['getMeetingsWithParticipants', 'getMeeting'];
export function deleteNote(note: Pick<MeetingNoteDto, 'id' | 'meetingId'>): Promise<void> {
    const url = getNoteUrl(note);
    return remove(url);
}

// ATTACHMENTS

addAttachment.operation = 'READ';
addAttachment.invalidates = ['getMeetingsWithParticipants', 'getMeeting'];
export function addAttachment(
    meetingId: string,
    createCommand: FilePayload & { private: boolean }
): Promise<MeetingDto> {
    return post(`${ENTRY}/${meetingId}/attachments`, { postData: createCommand });
}

deleteAttachment.operation = 'READ';
deleteAttachment.invalidates = ['getMeetingsWithParticipants', 'getMeeting'];
export function deleteAttachment(meetingId: string, attachmentId: string): Promise<MeetingDto> {
    return remove(`${ENTRY}/${meetingId}/attachments/${attachmentId}`);
}

triggerCsvExport.operation = 'NO_OPERATION';
export function triggerCsvExport(data: { from: string | undefined; until: string | undefined }) {
    return post<DownloadableFile>(`${ENTRY}/export-content-csv?from=${data.from || ''}&until=${data.until || ''}`);
}
